/* eslint-disable no-restricted-syntax */
import { ColorSchemes, SizeNames, PopperPlacements } from '@/utils/enum'
import { contains } from '@/utils/tools'

export function validateColorScheme(value) {
  return Object.values(ColorSchemes).includes(value)
}

export function validateSizeName(value) {
  return Object.values(SizeNames).includes(value)
}

export function validatePopperPlacement(value) {
  return Object.values(PopperPlacements).includes(value)
}

/**
 *
 * Builds the validator function for vue properties that are dependent on a gql fragment as input
 * This function works in conjunction with co-location of fragments as this allows for easy verification that the data
 * served to a component contains all information needed.
 *
 * It works similar to that of graphql-anywhere utility functions propType and check. But it's check function is
 * broken and its propType is specific to react and not vue.
 * details of code here https://github.com/doomsower/graphql-anywhere/blob/master/src/utilities.ts
 * @prop {Object} fragment an apollo fragment generated by gql
 * @return {Function} vuejs validator function
 */
export function graphqlFragmentValidator(fragment) {
  function getFragment(name) {
    for (const frag of fragment.definitions) {
      if (frag.name.value === name) return frag
    }
    return {}
  }

  function fragmentToObject(frag) {
    let obj = {}
    if (frag.selectionSet === undefined) return obj

    for (const field of frag.selectionSet.selections) {
      if (field.kind === 'FragmentSpread') {
        obj = {
          ...obj,
          ...fragmentToObject(getFragment(field.name.value)),
        }
      } else {
        obj = {
          ...obj,
          ...{
            [field?.alias?.value ?? field.name.value]: {
              ...fragmentToObject(field),
            },
          },
        }
      }
    }
    return obj
  }

  function assertTruthy(v, data, property) {
    if (v) {
      return true
    }
    throw new Error(
      `Expected ${JSON.stringify(data)} to contain ${property} at root level but found ${v}`,
    )
  }

  function containsProps(data, fragObj) {
    if (data === null) return true // We have reached the end of our set,
    for (const prop in fragObj) {
      if (data[prop] instanceof Array) {
        for (const d of data[prop]) {
          containsProps(d, fragObj[prop])
        }
      } else {
        assertTruthy(contains(data, prop, 0, true), data, prop)
        containsProps(data[prop], fragObj[prop])
      }
    }
    return true
  }
  const fragObj = fragmentToObject(fragment.definitions[0])
  function validator(data) {
    try {
      return containsProps(data, fragObj)
    } catch (e) {
      return false
    }
  }

  return validator
}
